type JsonRecord = Record<string, any>;

const BLOCK_ASSET_PLAN_FIELDS = new Set([
  'latestAssetPlanId',
  'assetPlanVersion',
  'assetPlanStatus',
  'assetHealth',
]);

const SCREEN_WORKSPACE_FIELDS = new Set([
  'aiAssets',
]);

const ALWAYS_STRIP_GENERATION_FIELDS = new Set([
  'generatedUrl',
  'generatedAt',
  'generationModel',
  'generationProvider',
  'negativePrompt',
  'promptPackage',
  'promptPackageId',
  'promptTemplate',
  'referenceImages',
]);

const ASSET_CONFIG_FIELDS = new Set([
  'aspectRatio',
  'experiments',
  'imageSize',
  'model',
  'openaiSize',
  'provider',
  'quality',
  'selectedExperimentId',
  'status',
]);

const ASSET_OBJECT_KEYS = [
  'asset',
  'assets',
  'heroAsset',
  'featuredMediaAsset',
  'imageAsset',
  'mediaAsset',
  'visualAsset',
];

export function sanitizeStrategyBlockForRegenerationInput<T extends JsonRecord>(block: T): T {
  return stripKeys(block, {
    blockFields: BLOCK_ASSET_PLAN_FIELDS,
    screenFields: SCREEN_WORKSPACE_FIELDS,
    stripInlineAssetGenerationFields: true,
  }) as T;
}

export function sanitizeStrategyScreenForRegenerationInput<T extends JsonRecord>(screen: T): T {
  return stripKeys(screen, {
    screenFields: SCREEN_WORKSPACE_FIELDS,
    stripInlineAssetGenerationFields: true,
  }) as T;
}

function stripKeys(
  value: unknown,
  options: {
    blockFields?: Set<string>;
    screenFields?: Set<string>;
    stripInlineAssetGenerationFields?: boolean;
  },
  path: string[] = [],
): unknown {
  if (Array.isArray(value)) {
    return value.map((item, index) => stripKeys(item, options, [...path, String(index)]));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const record = value as JsonRecord;
  const result: JsonRecord = {};
  const currentKey = path[path.length - 1] ?? '';
  const isAssetObject = options.stripInlineAssetGenerationFields === true
    && looksLikeAssetGenerationObject(record, currentKey);

  for (const [key, child] of Object.entries(record)) {
    if (path.length === 0 && options.blockFields?.has(key)) continue;
    if (options.screenFields?.has(key)) continue;
    if (options.stripInlineAssetGenerationFields === true) {
      if (ALWAYS_STRIP_GENERATION_FIELDS.has(key)) continue;
      if (isAssetObject && ASSET_CONFIG_FIELDS.has(key)) continue;
    }

    const next = stripKeys(child, options, [...path, key]);
    if (next !== undefined) result[key] = next;
  }

  return result;
}

function looksLikeAssetGenerationObject(record: JsonRecord, key: string): boolean {
  if (ASSET_OBJECT_KEYS.some((assetKey) => key === assetKey || key.endsWith(assetKey))) {
    return true;
  }

  return [
    'generatedUrl',
    'negativePrompt',
    'promptPackage',
    'promptTemplate',
    'referenceImages',
    'selectedExperimentId',
  ].some((field) => Object.prototype.hasOwnProperty.call(record, field));
}
